import clsx from "clsx";
import { format } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import TagManager from "react-gtm-module";

import Button from "../button";
import Input from "../input";

import Select from "../select";
import Textarea from "../textarea";
import DateSelect from "../date-select";
import DateTimeSelect from "../date-time-select";

import { MobileForm, MobileFormControls } from "../mobile-form";
import { activeCampaignSubmit } from "../../lib/forms";

const formatDate = (day) => {
  return format(day, "yyyy-MM-dd");
};

// Active Campaign field names.
const fieldMap = {
  companyName: "field[68]",
  bookingDate: "field[100]",
  bookingTime: "field[101]",
  location: "field[102]",
  attendees: "field[103]",
  details: "field[104]",
  subscribe: "field[24][]"
};

const BoardroomMobileForm = ({ locations }) => {
  const [error, setError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showMobileForm, setShowMobileForm] = useState(false);

  const {
    register,
    formState,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset
  } = useForm({
    mode: "onChange",
    // Default Active Campaign values for this
    // specific form.
    defaultValues: {
      u: "31",
      f: "31",
      s: "0",
      m: "0",
      act: "sub",
      v: "2",
      [fieldMap.bookingDate]: ""
    }
  });

  watch();

  const onSubmit = (data) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "contact_us",
        type: "boardroom-booking",
        attendees: data[fieldMap.attendees],
        location: data[fieldMap.location],
        firstname: data.firstname,
        lastname: data.lastname,
        phone: data.phone,
        email: data.email
      }
    });

    activeCampaignSubmit(data, false)
      .then(() => {
        setShowConfirmation(true);
      })
      .catch((e) => setError(e));
  };

  const handleFormReset = () => {
    reset();
    setShowConfirmation(false);
    setShowMobileForm(false);
    setError("");
  };

  const { errors } = formState;

  const bookingDateField = fieldMap.bookingDate.split("[")[1].slice(0, -1);

  const getError = (field) => {
    const fieldNum = field.split("[")[1].slice(0, -1);

    if (errors.field && errors.field[fieldNum]) {
      return errors.field[fieldNum].message;
    }

    return null;
  };

  return (
    <MobileForm showMobileForm={showMobileForm}>
      <form className={clsx("p-6")} onSubmit={handleSubmit(onSubmit)}>
        {!showConfirmation && (
          <>
            <h2 className="font-bold text-3xl text-center">
              Interested in booking a<br /> boardroom?
            </h2>

            <div className="my-4">
              <div className="form-group-inline">
                <Input
                  type="text"
                  placeholder="First name *"
                  {...register("firstname", {
                    required: "First name is required"
                  })}
                  errorText={errors.firstname && errors.firstname.message}
                  inline
                />
                <Input
                  type="text"
                  placeholder="Last name *"
                  {...register("lastname", {
                    required: "Last name is required"
                  })}
                  errorText={errors.lastname && errors.lastname.message}
                  inline
                />
              </div>

              <Input
                type="text"
                placeholder="Work email address *"
                {...register("email", { required: "Email is required" })}
                errorText={errors.email && errors.email.message}
              />

              <Input
                type="text"
                placeholder="Phone number *"
                {...register("phone", {
                  required: "Phone number is required"
                })}
                errorText={errors.phone && errors.phone.message}
              />
              <Input
                type="text"
                placeholder="Company name *"
                {...register(fieldMap.companyName, {
                  required: "Company name is required"
                })}
                errorText={getError(fieldMap.companyName)}
              />

              <div className="form-group-inline">
                <DateSelect
                  {...register(fieldMap.bookingDate, {
                    required: "Booking date is required"
                  })}
                  placeholder="Desired Booking Date *"
                  selectedDate={getValues().field?.[bookingDateField]}
                  disabledDates={(day) => day < new Date()}
                  onChange={(day) => {
                    setValue(fieldMap.bookingDate, formatDate(day));
                  }}
                  errorText={getError(fieldMap.bookingDate)}
                  inline
                />

                <DateTimeSelect
                  {...register(fieldMap.bookingTime, {
                    required: "Booking time is required"
                  })}
                  date={getValues().field?.[bookingDateField]}
                  type="text"
                  placeholder="Desired Booking Time *"
                  onChange={(e) => {
                    setValue(fieldMap.bookingTime, e.target.value);
                  }}
                  errorText={getError(fieldMap.bookingTime)}
                  inline
                />
              </div>

              <Select
                {...register(fieldMap.location, {
                  required: "Location is required"
                })}
                errorText={getError(fieldMap.location)}
              >
                <option value="" disabled selected>
                  Select your location of interest *
                </option>
                {locations.map((location) => (
                  <option
                    value={`${location.address1}, ${location.city} ${location.province}`}
                    key={location.slug}
                  >
                    {location.address1}, {location.city} {location.province}
                  </option>
                ))}
              </Select>

              <Input
                type="text"
                placeholder="Amount of attendees"
                {...register(fieldMap.attendees)}
              />

              <Textarea
                type="text"
                placeholder="Please share any additional information"
                rows="3"
                {...register(fieldMap.details)}
              />
            </div>

            {error && <p>{error}</p>}
          </>
        )}

        {showConfirmation && (
          <>
            <h2 className="font-bold text-2xl text-center">Thank you!</h2>

            <p className="text-md my-4 text-center font-light">
              A member of our team will be in touch with you shortly!
            </p>
          </>
        )}

        <MobileFormControls>
          {!showMobileForm && (
            <Button
              type="button"
              className="w-full my-2"
              outline
              onClick={() => setShowMobileForm(true)}
            >
              Inquire
            </Button>
          )}

          {showMobileForm && !showConfirmation && (
            <>
              <Button
                type="submit"
                className="w-full my-2"
                outline
                onClick={() => setShowMobileForm(true)}
              >
                Submit
              </Button>
              <Button
                type="button"
                className="w-full my-2"
                outline
                onClick={handleFormReset}
              >
                Cancel
              </Button>
            </>
          )}

          {showConfirmation && (
            <Button
              type="button"
              className="w-full my-2"
              outline
              onClick={handleFormReset}
            >
              Close
            </Button>
          )}
        </MobileFormControls>
      </form>
    </MobileForm>
  );
};

export default BoardroomMobileForm;
